import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useBrandLanguage } from '@context/brand-language/context';
import { useJotBillAccess } from '@features/billing/hooks/useJotBillAccess';
import { PrescribingNotificationsBell } from '@features/eprescriptions';
import { useFeatureLinksAccess } from '@features/knowledge-base/links';
import { guideEntryState } from '@features/knowledge-base/utils/guideNavigation';
import { GUIDE_PATH } from '@features/knowledge-base/utils/guideUrls';
import { useNotificationSummaryPolling } from '@features/notifications';
import { useReportsAccess } from '@features/reports';
import {
  useCan,
  useEmailVerified,
  useFeatureFlag,
  useOnboardingStep,
  useOrganizationInfo,
  usePaymentStatus,
  useSubjectLanguageVariants,
  useUserState,
} from '@features/user';
import { useNavTutorialsLabelExperiment } from '@hooks/useNavTutorialsLabelExperiment';
import { useRecordingSession } from '@hooks/useRecordingSession';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  HelpOutlined as HelpIcon,
  Menu as MenuIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import VideocamIcon from '@mui/icons-material/Videocam';
import {
  Alert,
  AppBar,
  Badge,
  Box,
  Button,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Modal,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { FeatureGates, useEventTracking, useFeatureGate } from '@services/statsig';

import { useRecordingContext } from '../../context/RecordingContext';
import { useUIState } from '../../context/uiState';
import { useModal } from '../../store/hooks';
import CustomButton from '../../styles/CustomButtons';
import { trackEvent } from '../../utils/analytics_utils';
import EmailVerificationPrompt from '../../Views/Shared/EmailVerificationPrompt';
import { AlertBanner } from '../AlertBanner';
import UserMenuButton from '../Buttons/UserMenuButton';
import { ConditionalTooltip } from '../ConditionalTooltip';
import { JotPsychWordmarkLogo } from '../Icons/Logo';
import { MODAL_TYPES } from '../modals';
import NavSearch from '../NavSearch/NavSearch';
import { useGlobalTutorial } from '../Tutorial/GlobalTutorialContext';

import type { RouteItem } from './Nav.types';
import { NavBranding } from './NavBranding';
import { NavDropdownItem } from './NavDropdownItem';
import { NavHelpMenu } from './NavHelpMenu';

export const Nav = () => {
  // Migrate to new user feature hooks
  const paymentStatus = usePaymentStatus();
  // Legacy V2 hides trialing users; payment_status is propagated to
  // every org member by the backend, so this covers non-billing-admin members too.
  const intakeHubV2Enabled = useFeatureGate(FeatureGates.INTAKE_HUB_V2);
  const intakeHubV3Enabled = useFeatureGate(FeatureGates.INTAKE_HUB_V3);
  // V3 onboarding must be reachable before the practice subscription is ready.
  const showIntakeHub = intakeHubV3Enabled || (intakeHubV2Enabled && paymentStatus !== 'trialing');
  const emailVerified = useEmailVerified();
  // ADR 0073 Phase 3 (GDN-1590): parity with hasBillingAccess.
  const canManageBilling = useCan('manage_billing');
  const canManageEligibility = useCan('manage_eligibility');
  // ADR 0073 Phase 2b (GDN-1589): subscription surfaces gate on
  // manage_subscription (practice_administrator only), not manage_billing.
  const canManageSubscription = useCan('manage_subscription');
  // ADR 0073 / GDN-2610: the global "Record" entry point gates on
  // `record_encounter_inputs` (clinician and clinical_staff) — note
  // authoring is gated separately inside the encounter view.
  const canCreateNotes = useCan('record_encounter_inputs');
  // Reports console: per-report role access resolved by the backend — the
  // nav item shows when the user can see at least one report.
  const { hasAccess: canSeeReports } = useReportsAccess();
  // Granular permissions so the "Your Practice" dropdown mirrors the /practice
  // tab set (PracticeLayout) instead of drifting from it. Each matches the gate
  // its tab uses: settings=manage_organization, appointment types, and
  // GDN-2187 scheduling=manage_org_calendar.
  const canManageOrganization = useCan('manage_organization');
  const canManageAppointmentTypes = useCan('manage_appointment_types');
  const canManageAppointmentLabels = useCan('manage_appointment_labels');
  const canManageScheduling = useCan('manage_org_calendar');
  // GDN-2187: feature-gated until rollout — hides the Scheduling entry (and its
  // tab/route) even for calendar managers while the gate is off (default-off).
  const schedulingFeatureEnabled = useFeatureGate(FeatureGates.PATIENT_SELF_SCHEDULING);
  // GDN-2482: consolidated Schedule & Documentation tab — visible when any of
  // its sub-sections is (mirrors PracticeLayout's gate disjunction). Includes
  // GDN-2187 Scheduling and the manage_appointment_labels permission.
  const canManageTemplates = useCan('manage_templates');
  const templateCatalogEnabled = useFeatureGate(FeatureGates.PRACTICE_TEMPLATE_CATALOG);
  const showScheduleAndDocumentation =
    (templateCatalogEnabled && canManageTemplates) ||
    canManageAppointmentTypes ||
    canManageAppointmentLabels ||
    (canManageScheduling && schedulingFeatureEnabled);
  const stripeConnectFeatureFlag = useFeatureFlag('stripe_connect');
  const { hasAccess: hasBillingFeature } = useJotBillAccess();
  // GDN-3226 (AC-2): the missing-payment-method worklist. Billing-shaped, so it
  // sits with the other worklists rather than under Your Practice, where the
  // requirement catalog lives.
  const patientRequirementsEnabled = useFeatureGate(FeatureGates.PATIENT_REQUIREMENTS);
  const generatedFirstNote = useOnboardingStep('generated_first_note');
  const { pluralCap: subjectLanguagePluralCap } = useSubjectLanguageVariants();
  const {
    startRecording,
    isLoadingAppointments,
    canStartRecording,
    requiresScheduledAppointmentForNoteCreation,
  } = useRecordingSession();
  const isRecordActionBlocked =
    requiresScheduledAppointmentForNoteCreation && (isLoadingAppointments || !canStartRecording);
  const tutorialsLabel = useNavTutorialsLabelExperiment();

  const userState = useUserState();
  // Solo orgs collapse the Members surface into the solo subscription page;
  // hide the Clinicians link from the nav so the route isn't reachable through
  // the dropdown. Mirrors the `PracticeLayout` Clinicians-tab hide.
  const isGroupPractice = useOrganizationInfo()?.group === true;

  // Poll notification summary for badge counts (3 min interval, app-wide)
  const { notesTotalCount: notificationBadgeCount, schedulingTotalCount } =
    useNotificationSummaryPolling();

  const location = useLocation();
  const navigate = useNavigate();
  const { navigation: navigationMetrics } = useEventTracking();
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState<boolean>(false);
  const { state: UIState, showAlertBanner } = useUIState();
  const { logout } = useAuth0();
  const theme = useTheme();
  const { open } = useModal();
  const { brandLanguage } = useBrandLanguage();
  const { platform } = UIState;
  const { hasTutorials, startTutorial } = useGlobalTutorial();

  const { recordingStatus, elapsedTime, allowRecorderControl } = useRecordingContext();

  // Custom breakpoint for nav drawer at 1100px
  const isDesktop = useMediaQuery('(min-width: 1100px)');

  // GDN-3695 (AC-1): read the access once here. Enabled, "Help Articles" goes
  // to the in-app guide and the Notion "Tutorials" controls disappear; closed
  // gate or holdout control arm, the nav is unchanged. The menu follows the arm
  // without counting as an exposure (GDN-3707).
  const { enabled: guideLinksEnabled } = useFeatureLinksAccess({ logExposure: false });
  const guidePath = guideLinksEnabled ? GUIDE_PATH : undefined;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileOpenMenuId, setMobileOpenMenuId] = useState<string | null>(null);
  const [hasOpenedDrawer, setHasOpenedDrawer] = useState(false);

  // Expand Documentation menu by default on first drawer open only
  useEffect(() => {
    if (drawerOpen && !hasOpenedDrawer) {
      setMobileOpenMenuId('DocumentationMenu');
      setHasOpenedDrawer(true);
    }
  }, [drawerOpen, hasOpenedDrawer]);

  const formatTime = (totalSeconds: number): string => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes)}:${seconds.toString().padStart(2, '0')}`;
  };

  const isRecorderPage =
    location.pathname.includes('/record') || location.pathname.includes('/encounters/'); // Check for both record and encounters paths

  const handleToggleSidebar = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleMobileHelpArticlesClick = () => {
    // Track help menu navigation
    navigationMetrics.clicked({
      menu_item: 'help_knowledgebase',
      source_path: location.pathname,
      destination_path: guidePath ?? 'external',
      is_dropdown: true,
    });
    // Keep legacy tracking for now
    trackEvent({
      event: 'help_knowledgebase_clicked',
      platform: platform,
    });
    setDrawerOpen(false);
  };

  const routeMap: RouteItem[] = [
    // 1. Schedule - direct link
    {
      path: '/schedule',
      pageName: 'Schedule',
      id: 'ScheduleView',
      testId: 'nav-schedule',
      // Pending self-serve booking + change requests (org-calendar managers
      // only; 0 for everyone else, so no badge shows). Draws managers to the
      // calendar, where BookingRequestsButton has the full queue.
      badgeCount: schedulingTotalCount,
    },
    {
      path: '/patients',
      pageName: subjectLanguagePluralCap,
      id: 'PatientProfiles',
      testId: 'nav-patient-profiles',
    },
    // 2. Documentation - dropdown
    {
      path: null,
      pageName: 'Documentation',
      id: 'DocumentationMenu',
      testId: 'nav-documentation-menu',
      isDropdown: true,
      badgeCount: notificationBadgeCount,
      children: [
        {
          path: '/notes',
          pageName: 'Notes',
          id: 'NotesView',
          testId: 'nav-notes',
          badgeCount: notificationBadgeCount,
        },
        {
          path: '/templates',
          pageName: 'Note Templates',
          id: 'TemplatesView',
          testId: 'nav-templates',
        },
        { path: '/labs', pageName: 'Labs', id: 'LabsView', testId: 'nav-labs' },
        { path: '/form-documents', pageName: 'Forms', id: 'FormsView', testId: 'nav-forms' },
        {
          path: '/documents',
          pageName: 'Other Docs',
          id: 'DocumentsView',
          testId: 'nav-documents',
        },
        {
          path: '/sent-documents',
          pageName: 'Sent Documents',
          id: 'SentDocumentsView',
          testId: 'nav-sent-documents',
        },
      ],
    },
    // 4. Billing + Auditing - dropdown
    {
      path: null,
      pageName: 'Billing + Auditing',
      id: 'BillingAuditingMenu',
      testId: 'nav-billing-auditing-menu',
      isDropdown: true,
      children: [
        {
          path: '/jotaudit',
          pageName: 'JotAudit',
          id: 'AuditView',
          testId: 'nav-audit',
        },
        {
          path: hasBillingFeature ? '/jotbill' : '/bill-insurance',
          pageName: hasBillingFeature ? 'JotBill' : 'Bill Insurance',
          id: 'BillInsurance',
          testId: 'nav-bill-insurance',
          condition: () => canManageBilling,
        },
        {
          path: '/jotbill?tab=patient-billing',
          pageName: `Bill ${subjectLanguagePluralCap}`,
          id: 'BillPatients',
          testId: 'nav-bill-patients',
          condition: () => canManageBilling,
        },
        {
          path: '/eligibility-checks',
          pageName: 'Eligibility',
          id: 'EligibilityChecksView',
          testId: 'nav-eligibility-checks',
          condition: () => canManageEligibility,
        },
        {
          path: '/patients/missing-payment-method',
          pageName: 'Missing payment methods',
          id: 'MissingPaymentMethods',
          testId: 'nav-missing-payment-methods',
          condition: () => patientRequirementsEnabled && canManageBilling,
        },
      ],
    },
    // 5. Your Practice - dropdown
    {
      path: null,
      pageName: 'Your Practice',
      id: 'YourPracticeMenu',
      testId: 'nav-your-practice-menu',
      isDropdown: true,
      // Order + per-item gates mirror the /practice tabs (PracticeLayout) so the
      // dropdown and the tab bar expose the same destinations for a given user.
      children: [
        {
          path: '/practice/clinicians',
          pageName: 'Members',
          id: 'CliniciansView',
          testId: 'nav-clinicians',
          // Members first for group orgs (primary configuration surface).
          // ADR 0073: manage_billing (was hasBillingAccess); solo orgs hide the
          // Members link (the surface collapses into the solo subscription page).
          condition: () => canManageBilling && isGroupPractice,
        },
        {
          path: '/practice/subscriptions',
          pageName: 'Subscription',
          id: 'SubscriptionsView',
          testId: 'nav-subscriptions',
          condition: () => canManageSubscription,
        },
        {
          path: '/intake/key-questions',
          pageName: 'Intake Hub',
          id: 'IntakeHub',
          testId: 'nav-intake-hub',
          condition: () => showIntakeHub,
        },
        {
          path: '/practice/billing-settings',
          pageName: 'Billing & Claims',
          id: 'BillingSettingsView',
          testId: 'nav-billing-settings',
          condition: () => hasBillingFeature && canManageBilling,
        },
        // {
        //   path: '/practice/payer-docs-center',
        //   pageName: 'Payer Docs Center',
        //   id: 'PayerDocsCenterView',
        //   testId: 'nav-payer-docs-center',
        //   condition: () => canManageBilling,
        // },
        {
          // GDN-2482: consolidated section — replaces the separate
          // Scheduling + Appointment Types + Appointment Labels entries.
          path: '/practice/schedule-documentation',
          pageName: 'Schedule & Documentation',
          id: 'ScheduleAndDocumentationView',
          testId: 'nav-schedule-documentation',
          condition: () => showScheduleAndDocumentation,
        },
        {
          path: '/practice/payments',
          pageName: 'Patient Payments',
          id: 'PaymentsView',
          testId: 'nav-payments',
          condition: () => stripeConnectFeatureFlag && canManageBilling,
        },
        {
          path: '/practice/settings',
          pageName: 'Practice Settings',
          id: 'PracticeSettingsView',
          testId: 'nav-practice-settings',
          condition: () => canManageOrganization,
        },
        {
          path: '/reports-analytics',
          pageName: 'Reports',
          id: 'ReportsAnalytics',
          testId: 'nav-reports-analytics',
          condition: () => canSeeReports,
        },
      ],
      // Show the section when any child is visible. NavDropdownItem renders its
      // button unconditionally, so keep this aligned with the child conditions.
      condition: () =>
        (canManageBilling && isGroupPractice) ||
        canManageSubscription ||
        showIntakeHub ||
        (hasBillingFeature && canManageBilling) ||
        showScheduleAndDocumentation ||
        (stripeConnectFeatureFlag && canManageBilling) ||
        canManageOrganization ||
        canSeeReports,
    },
    // 6. Record - action button
    {
      path: undefined, // Remove path to prevent automatic navigation
      pageName: 'Record',
      id: 'EncounterRecordAction',
      testId: 'nav-record',
      customStyle: { color: theme.palette.error.main },
      onClick: () => {
        startRecording();
      },
      // ADR 0073: hide for users who cannot author notes.
      condition: () => canCreateNotes,
    },
  ];

  const getButtonStyle = (route: (typeof routeMap)[number]) => {
    // Check if it's a dropdown menu (has children but no path)
    if (!route.path && route.children) {
      // Type guard - only RouteItem can have children
      const routeItem = route;
      const isChildActive = routeItem.children?.some(
        child =>
          child.path !== null &&
          (location.pathname === child.path || location.pathname.startsWith(child.path)),
      );
      return {
        fontWeight: isChildActive ? 'bold' : 'normal',
        color: isChildActive ? theme.palette.primary.light : 'textColors.lightHeader',
      };
    }

    // External links should never show as active
    const routeItem = route;
    if (routeItem.externalUrl) {
      const baseStyle = {
        fontWeight: 'normal',
        color: 'textColors.lightHeader' as const,
      };
      if (routeItem.customStyle) {
        return { ...baseStyle, ...routeItem.customStyle };
      }
      return baseStyle;
    }

    const isActive =
      route.path &&
      (location.pathname === route.path ||
        (route.path === '/encounters/active' && location.pathname.startsWith('/encounters/')) ||
        (location.pathname.startsWith(route.path) && route.path !== '/'));
    const baseStyle = {
      fontWeight: isActive ? 'bold' : 'normal',
      color: isActive ? theme.palette.primary.light : 'textColors.lightHeader',
    };

    // Only RouteItem can have customStyle
    if (routeItem.customStyle) {
      return { ...baseStyle, ...routeItem.customStyle };
    }

    return baseStyle;
  };

  useEffect(() => {
    // Calculate days since account creation
    const daysSinceCreated = () => {
      if (!userState?.createdAt) return 0;
      const createdDate = new Date(userState.createdAt);
      const now = new Date();
      const timeDiff = now.getTime() - createdDate.getTime();
      return Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    };

    // Check if we should show the onboarding help banner
    const hasNotGeneratedFirstNote = !generatedFirstNote;
    const hasBeenMoreThanADay = daysSinceCreated() >= 1;
    const bannerNotShown = !localStorage.getItem('onboardingBannerShown');

    if (hasNotGeneratedFirstNote && hasBeenMoreThanADay && bannerNotShown) {
      // Small delay for better UX (5 seconds)
      const timerId = setTimeout(() => {
        localStorage.setItem('onboardingBannerShown', 'true');
        showAlertBanner(
          'Need help getting started? Click here to schedule 15 minutes with our product team.',
          'warning',
          false,
          () => {
            window.open(
              `https://calendly.com/d/cqyn-mhr-yw5/jotpsych-onboarding-support`,
              '_blank',
            );
          },
        );
      }, 5000); // Reduced to 5 seconds

      return () => {
        clearTimeout(timerId);
      };
    }
  }, [userState?.createdAt, generatedFirstNote, showAlertBanner]);

  const [toolbarOpen, setToolbarOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Add state to track if search has text
  const [hasSearchText, setHasSearchText] = useState(false);

  // Function to handle hover start for search icon
  const handleHoverStart = () => {
    setIsHovering(true);

    // Clear any existing timer
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }

    // Set new timer to open toolbar after 150ms
    hoverTimerRef.current = setTimeout(() => {
      setToolbarOpen(true);
    }, 150);
  };

  // Function to handle hover end for search icon
  const handleHoverEnd = () => {
    setIsHovering(false);

    // Clear the timer if the mouse leaves before 150ms
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  };

  // Function to handle search text changes
  const handleSearchTextChange = (hasText: boolean) => {
    setHasSearchText(hasText);
  };

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
      }
    };
  }, []);

  // Add useEffect to handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Command+P (Mac) or Ctrl+P (Windows/Linux)
      if ((event.metaKey || event.ctrlKey) && event.key === 'p') {
        // Prevent default browser behavior (print dialog)
        event.preventDefault();

        // Always toggle the toolbar (open if closed, close if open)
        setToolbarOpen(prevState => !prevState);

        // Clear any hover timer that might be active
        if (hoverTimerRef.current) {
          clearTimeout(hoverTimerRef.current);
          hoverTimerRef.current = null;
        }
      }
    };

    // Add event listener
    window.addEventListener('keydown', handleKeyDown);

    // Clean up event listener
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <>
      <AppBar
        position="sticky"
        color="primary"
        sx={{
          bgcolor: 'grey.100',
        }}
        elevation={1}
      >
        <Toolbar
          sx={{
            minHeight: {
              xs: theme.layout.navHeight.xs,
              sm: theme.layout.navHeight.sm,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'space-between',
              flex: 1,
            }}
          >
            <Box
              color="primary"
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <IconButton
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={handleToggleSidebar}
                sx={{ display: isDesktop ? 'none' : 'flex', color: 'black' }}
              >
                <MenuIcon />
              </IconButton>

              {/* Search icon that triggers toolbar */}
              <Tooltip
                title={`${navigator.userAgent.indexOf('Mac') !== -1 ? '⌘' : 'Ctrl'} + P`}
                placement="left"
                arrow
              >
                <IconButton
                  color="inherit"
                  aria-label="search"
                  onClick={() => {
                    setToolbarOpen(true);
                  }}
                  onMouseEnter={handleHoverStart}
                  onMouseLeave={handleHoverEnd}
                  sx={{
                    color: toolbarOpen ? theme.palette.primary.main : theme.palette.grey[700],
                    mr: 1,
                    transition: 'all 0.2s ease',
                    transform: isHovering ? 'scale(1.2)' : 'scale(1)',
                    animation: isHovering ? 'searchPulse 1.2s ease infinite' : 'none',
                    '@keyframes searchPulse': {
                      '0%': { opacity: 0.7 },
                      '50%': { opacity: 1 },
                      '100%': { opacity: 0.7 },
                    },
                  }}
                >
                  <SearchIcon />
                </IconButton>
              </Tooltip>

              <NavBranding />
              {/* {isLeftSideBarPage && (
                <CustomButton
                  sx={{ display: { xs: "inherit", md: "none" } }}
                  onClick={() => {
                    setState((prevState: UIStateType) => ({
                      ...prevState,
                      navigation: { currentPage: "home" },
                    }));
                    navigate("/");
                  }}
                  size="large"
                  aria-label="logo-home-button"
                >
                  Home
                </CustomButton>
              )} */}

              <Box
                className="left-nav-buttons-container"
                sx={{ display: isDesktop ? 'flex' : 'none' }}
              >
                {routeMap
                  .filter(route => !route.condition || route.condition())
                  .map(route => {
                    // Handle dropdown menus (Customization and Org)
                    if (route.isDropdown) {
                      return (
                        <NavDropdownItem
                          key={route.id}
                          route={route}
                          onNavigate={(path, pageName) => {
                            navigationMetrics.clicked({
                              menu_item: pageName.toLowerCase().replace(/\s+/g, '_'),
                              source_path: location.pathname,
                              destination_path: path,
                              is_dropdown: true,
                            });
                          }}
                        />
                      );
                    }

                    // Regular menu items
                    // Determine component type and props based on route type
                    const isExternalLink = Boolean(route.externalUrl);
                    const isInternalLink = Boolean(route.path);
                    const isRouteDisabled =
                      route.id === 'EncounterRecordAction' && isRecordActionBlocked;

                    // Build component props conditionally
                    let componentProps: Record<string, unknown> = {};
                    if (isExternalLink) {
                      componentProps = {
                        component: 'a',
                        href: route.externalUrl,
                        ...(route.externalLinkProps || {}),
                      };
                    } else if (isInternalLink && route.path) {
                      componentProps = {
                        component: Link,
                        to: route.path,
                      };
                    }

                    return (
                      <CustomButton
                        key={route.path || route.externalUrl || route.id}
                        data-testid={route.testId}
                        aria-busy={
                          route.id === 'EncounterRecordAction' ? isLoadingAppointments : undefined
                        }
                        aria-disabled={isRouteDisabled || undefined}
                        disabled={isRouteDisabled}
                        sx={getButtonStyle(route)}
                        variant="text"
                        {...componentProps}
                        onClick={() => {
                          if (isRouteDisabled) return;
                          route.onClick?.();

                          // Track all navigation clicks including action-only routes
                          navigationMetrics.clicked({
                            menu_item: route.pageName.toLowerCase().replace(/\s+/g, '_'),
                            source_path: location.pathname,
                            destination_path: route.path || route.externalUrl || 'action',
                            is_dropdown: false,
                          });
                        }}
                      >
                        {route.badge ? (
                          <Badge
                            color="info"
                            badgeContent="NEW"
                            anchorOrigin={{
                              vertical: 'top',
                              horizontal: 'left',
                            }}
                            sx={{
                              '& .MuiBadge-badge': {
                                fontSize: '0.5rem',
                                padding: '0 6px',
                                fontWeight: 'bold',
                                transform: 'scale(1) translate(30%, -80%)',
                              },
                            }}
                          >
                            {route.pageName}
                          </Badge>
                        ) : route.badgeCount && route.badgeCount > 0 ? (
                          <Badge
                            color="error"
                            badgeContent={route.badgeCount}
                            max={99}
                            sx={{
                              '& .MuiBadge-badge': {
                                fontSize: '0.65rem',
                                height: 18,
                                minWidth: 18,
                              },
                            }}
                          >
                            {route.pageName}
                          </Badge>
                        ) : (
                          route.pageName
                        )}
                      </CustomButton>
                    );
                  })}
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { md: 0, lg: 1, xl: 2 },
            }}
          >
            <Stack
              direction={'row'}
              sx={{
                gap: { md: 2, lg: 3, xl: 4 },
                display: isDesktop ? 'flex' : 'none',
                alignItems: 'center',
              }}
            >
              {/* Desktop-only items (lg+): Next Appointment Badge, Recording Status, Email Verification */}
              <Box
                sx={{
                  display: { xs: 'none', lg: 'flex' },
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                {/* Recording Status - Redesigned to match NextAppointmentBadge */}
                {!isRecorderPage && recordingStatus.isRecording && allowRecorderControl && (
                  <Box
                    onClick={() => {
                      // Track quick action navigation
                      navigationMetrics.quickAction({
                        action_type: 'record',
                        source_path: location.pathname,
                      });
                      startRecording();
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      bgcolor: 'error.main',
                      color: 'white',
                      borderRadius: '6px',
                      p: '4px 8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: 1,
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 2,
                      },
                    }}
                  >
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: 'white',
                          animation: 'pulse 1s infinite',
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 'medium',
                        }}
                      >
                        {formatTime(elapsedTime)}
                      </Typography>
                    </Stack>
                  </Box>
                )}

                {/* Email Verification */}
                {paymentStatus === 'active' && !emailVerified && (
                  <ConditionalTooltip
                    condition={true}
                    title="Click here to verify your email."
                    placement="bottom"
                    arrow
                  >
                    <Alert
                      severity="error"
                      sx={{
                        py: 0,
                        px: 1,
                        cursor: 'pointer',
                        height: 'auto',
                        borderRadius: '100px',
                        border: '1px solid red',
                        '& .MuiAlert-message': {
                          width: '100%',
                        },
                      }}
                      onClick={() => {
                        setShowEmailVerificationModal(true);
                      }}
                    >
                      Email not verified.
                    </Alert>
                  </ConditionalTooltip>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {/* Tutorials Button */}
                {!guideLinksEnabled && (
                  <CustomButton
                    variant="text"
                    component="a"
                    href="https://jotpsych.notion.site/How-to-Use-JotPsych-2a08b51156598037b2f0e2457e372414"
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<VideocamIcon sx={{ fontSize: '26px !important' }} />}
                    onClick={() => {
                      // Track tutorial link clicks
                      navigationMetrics.clicked({
                        menu_item: 'tutorials',
                        source_path: location.pathname,
                        destination_path: 'external',
                        is_dropdown: false,
                      });
                    }}
                  >
                    {tutorialsLabel}
                  </CustomButton>
                )}
                {/* Help Menu */}
                <NavHelpMenu guidePath={guidePath} />
              </Box>
            </Stack>

            {/* Prescribing notifications bell (GDN-1062) — renders only for clinicians
                connected to DoseSpot with non-zero pending prescribing work */}
            <PrescribingNotificationsBell />

            {/* User Menu Button */}
            <UserMenuButton />
          </Box>
        </Toolbar>

        {/* Fixed position container for toolbar - prevents layout shifts */}
        <Box
          sx={{
            position: 'fixed',
            top: {
              xs: `${theme.layout.navHeight.xs}px`,
              sm: `${theme.layout.navHeight.sm}px`,
            },
            left: 0,
            right: 0,
            zIndex: 100,
            pointerEvents: toolbarOpen ? 'auto' : 'none',
          }}
        >
          {/* Expandable toolbar */}
          <Box
            sx={{
              height: toolbarOpen ? '60px' : '0px',
              overflow: 'hidden',
              transition: 'height 0.3s ease, box-shadow 0.3s ease',
              backgroundColor: theme.palette.grey[100],
              boxShadow: toolbarOpen ? '0px 4px 10px rgba(0, 0, 0, 0.1)' : 'none',
              borderTop: toolbarOpen ? `1px solid ${theme.palette.grey[300]}` : 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              padding: '0 24px', // Keep padding consistent
              paddingTop: toolbarOpen ? undefined : 0,
              paddingBottom: toolbarOpen ? undefined : 0,
              width: '100%',
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: '100%',
                opacity: toolbarOpen ? 1 : 0,
                transition: 'opacity 0.3s ease',
              }}
              onMouseLeave={() => {
                // Only close the toolbar if there's no search text
                if (!hasSearchText) {
                  setToolbarOpen(false);
                }
              }}
            >
              <NavSearch
                onClose={() => {
                  setToolbarOpen(false);
                }}
                toolbarOpen={toolbarOpen}
                onSearchTextChange={handleSearchTextChange}
                setIsHovering={setIsHovering}
              />
            </Box>
          </Box>
        </Box>

        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
          }}
          sx={{
            display: isDesktop ? 'none' : 'block',
            '& .MuiDrawer-paper': {
              minWidth: '250px',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          {/* Drawer Header with Logo and Close Button */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Box
              onClick={() => {
                navigationMetrics.clicked({
                  menu_item: 'logo',
                  source_path: location.pathname,
                  destination_path: '/',
                  is_dropdown: false,
                });
                navigate('/');
                setDrawerOpen(false);
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                flexShrink: 0,
                minWidth: '120px',
              }}
            >
              <JotPsychWordmarkLogo size={100} />
            </Box>
            <IconButton
              onClick={() => {
                setDrawerOpen(false);
              }}
              aria-label="close drawer"
              sx={{ color: 'textColors.lightHeader' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Drawer Content - Scrollable List */}
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <List>
              {routeMap
                .filter(route => !route.condition || route.condition())
                .map(route => {
                  // Handle dropdown menus in mobile drawer
                  if (route.isDropdown) {
                    const isOpen = mobileOpenMenuId === route.id;
                    const toggleOpen = () => {
                      setMobileOpenMenuId(isOpen ? null : route.id);
                    };

                    return (
                      <React.Fragment key={route.id}>
                        <ListItem
                          component={ListItemButton}
                          onClick={toggleOpen}
                          sx={getButtonStyle(route)}
                        >
                          <ListItemText
                            primary={
                              route.badgeCount && route.badgeCount > 0 ? (
                                <Badge
                                  color="error"
                                  badgeContent={route.badgeCount}
                                  max={99}
                                  sx={{
                                    '& .MuiBadge-badge': {
                                      fontSize: '0.65rem',
                                      height: 18,
                                      minWidth: 18,
                                    },
                                  }}
                                >
                                  {route.pageName}
                                </Badge>
                              ) : (
                                route.pageName
                              )
                            }
                          />
                          {isOpen ? (
                            <ExpandMoreIcon sx={{ transform: 'rotate(180deg)' }} />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </ListItem>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding>
                            {route.children
                              ?.filter(child => !child.condition || child.condition())
                              .map(child => {
                                const isPlaceholder = child.path === null;
                                if (isPlaceholder) {
                                  return (
                                    <ListItem key={child.id} disablePadding>
                                      <ListItemButton
                                        disabled
                                        sx={{
                                          pl: 4,
                                          ...getButtonStyle({ ...child, customStyle: undefined }),
                                          color: theme.palette.text.disabled,
                                          cursor: 'not-allowed',
                                        }}
                                      >
                                        <ListItemText
                                          primary={
                                            <>
                                              {child.pageName}
                                              <span
                                                style={{
                                                  marginLeft: '8px',
                                                  fontSize: '0.75rem',
                                                  opacity: 0.7,
                                                }}
                                              >
                                                (Coming soon)
                                              </span>
                                            </>
                                          }
                                        />
                                      </ListItemButton>
                                    </ListItem>
                                  );
                                }
                                // TypeScript type narrowing - we know path is not null here
                                if (!child.path) return null;
                                const childPath: string = child.path;
                                return (
                                  <ListItem
                                    key={child.id}
                                    component={Link}
                                    to={childPath}
                                    onClick={() => {
                                      // Track mobile dropdown navigation
                                      navigationMetrics.clicked({
                                        menu_item: child.pageName
                                          .toLowerCase()
                                          .replace(/\s+/g, '_'),
                                        source_path: location.pathname,
                                        destination_path: childPath,
                                        is_dropdown: true,
                                      });
                                      setDrawerOpen(false);
                                    }}
                                    sx={{
                                      pl: 4,
                                      ...getButtonStyle({ ...child, customStyle: undefined }),
                                    }}
                                  >
                                    <ListItemText
                                      primary={
                                        child.badgeCount && child.badgeCount > 0 ? (
                                          <Badge
                                            color="error"
                                            badgeContent={child.badgeCount}
                                            max={99}
                                            sx={{
                                              '& .MuiBadge-badge': {
                                                fontSize: '0.65rem',
                                                height: 18,
                                                minWidth: 18,
                                              },
                                            }}
                                          >
                                            {child.pageName}
                                          </Badge>
                                        ) : (
                                          child.pageName
                                        )
                                      }
                                    />
                                  </ListItem>
                                );
                              })}
                          </List>
                        </Collapse>
                      </React.Fragment>
                    );
                  }

                  // Regular menu items in drawer
                  // Determine component type and props based on route type
                  const isMobileExternalLink = Boolean(route.externalUrl);
                  const isMobileInternalLink = Boolean(route.path);

                  // Build component props conditionally
                  let mobileComponentProps: Record<string, unknown> = {
                    component: ListItemButton,
                  };
                  if (isMobileExternalLink) {
                    mobileComponentProps = {
                      component: 'a',
                      href: route.externalUrl,
                      ...(route.externalLinkProps || {}),
                    };
                  } else if (isMobileInternalLink && route.path) {
                    mobileComponentProps = {
                      component: Link,
                      to: route.path,
                    };
                  }

                  return (
                    <ListItem
                      key={route.path || route.externalUrl || route.id}
                      {...mobileComponentProps}
                      data-testid={route.testId}
                      onClick={() => {
                        route.onClick?.();

                        // Track all navigation clicks including action-only routes
                        navigationMetrics.clicked({
                          menu_item: route.pageName.toLowerCase().replace(/\s+/g, '_'),
                          source_path: location.pathname,
                          destination_path: route.path || route.externalUrl || 'action',
                          is_dropdown: false,
                        });

                        setDrawerOpen(false);
                      }}
                      sx={getButtonStyle(route)}
                    >
                      <ListItemText
                        primary={
                          route.badge ? (
                            <Badge
                              color="error"
                              badgeContent="NEW"
                              anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                              }}
                              sx={{
                                '& .MuiBadge-badge': {
                                  fontSize: '0.7rem',
                                  height: '20px',
                                  minWidth: '35px',
                                  padding: '0 6px',
                                  fontWeight: 'bold',
                                  transform: 'scale(1) translate(50%, 40%)',
                                },
                              }}
                            >
                              {route.pageName}
                            </Badge>
                          ) : route.badgeCount && route.badgeCount > 0 ? (
                            <Badge
                              color="error"
                              badgeContent={route.badgeCount}
                              max={99}
                              sx={{
                                '& .MuiBadge-badge': {
                                  fontSize: '0.65rem',
                                  height: 18,
                                  minWidth: 18,
                                },
                              }}
                            >
                              {route.pageName}
                            </Badge>
                          ) : (
                            route.pageName
                          )
                        }
                      />
                    </ListItem>
                  );
                })}
              {!guideLinksEnabled && (
                <ListItem
                  component="a"
                  href="https://jotpsych.notion.site/How-to-Use-JotPsych-2a08b51156598037b2f0e2457e372414"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    // Track tutorial link clicks from mobile drawer
                    navigationMetrics.clicked({
                      menu_item: 'tutorials',
                      source_path: location.pathname,
                      destination_path: 'external',
                      is_dropdown: false,
                    });
                    setDrawerOpen(false);
                  }}
                  sx={{ fontWeight: 'normal', color: 'textColors.lightHeader' }}
                >
                  <ListItemText primary={tutorialsLabel} />
                </ListItem>
              )}
              {/* Help Menu Section - Collapsible */}
              <ListItem
                component={ListItemButton}
                onClick={() => {
                  setMobileOpenMenuId(mobileOpenMenuId === 'HelpMenu' ? null : 'HelpMenu');
                }}
                sx={{ fontWeight: 'normal', color: 'textColors.lightHeader' }}
              >
                <HelpIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
                <ListItemText primary="Help" />
                {mobileOpenMenuId === 'HelpMenu' ? (
                  <ExpandMoreIcon sx={{ transform: 'rotate(180deg)' }} />
                ) : (
                  <ExpandMoreIcon />
                )}
              </ListItem>
              <Collapse in={mobileOpenMenuId === 'HelpMenu'} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {hasTutorials && (
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => {
                          startTutorial();
                          // Track help menu navigation
                          navigationMetrics.clicked({
                            menu_item: 'help_tutorial',
                            source_path: location.pathname,
                            destination_path: 'modal',
                            is_dropdown: true,
                          });
                          // Keep legacy tracking for now
                          trackEvent({
                            event: 'help_tutorial_clicked',
                            platform: platform,
                          });
                          setDrawerOpen(false);
                        }}
                        sx={{ pl: 4, fontWeight: 'normal', color: 'textColors.lightHeader' }}
                      >
                        <ListItemText primary="Show Page Tutorial" />
                      </ListItemButton>
                    </ListItem>
                  )}
                  {guidePath ? (
                    <ListItem
                      component={Link}
                      to={guidePath}
                      state={guideEntryState(location)}
                      onClick={handleMobileHelpArticlesClick}
                      sx={{
                        pl: 4,
                        fontWeight: 'normal',
                        color: 'textColors.lightHeader',
                        textDecoration: 'none',
                      }}
                    >
                      <ListItemText primary="Help Articles" />
                    </ListItem>
                  ) : (
                    <ListItem
                      component="a"
                      href="https://jotpsych.frontkb.com/en"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleMobileHelpArticlesClick}
                      sx={{
                        pl: 4,
                        fontWeight: 'normal',
                        color: 'textColors.lightHeader',
                        textDecoration: 'none',
                      }}
                    >
                      <ListItemText primary="Help Articles" />
                    </ListItem>
                  )}
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => {
                        // Track help menu navigation
                        navigationMetrics.clicked({
                          menu_item: 'help_consent_information',
                          source_path: location.pathname,
                          destination_path: 'modal',
                          is_dropdown: true,
                        });
                        // Keep legacy tracking for now
                        trackEvent({
                          event: 'help_consent_information_clicked',
                          platform: platform,
                        });
                        // Open consent modal
                        open(MODAL_TYPES.CONSENT_INFORMATION, {});
                        setDrawerOpen(false);
                      }}
                      sx={{ pl: 4, fontWeight: 'normal', color: 'textColors.lightHeader' }}
                    >
                      <ListItemText primary="Patient Consent & Privacy" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem
                    component="a"
                    href={`https://${brandLanguage.brandSite}/onboarding-support`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      // Track help menu navigation
                      navigationMetrics.clicked({
                        menu_item: 'help_onboarding',
                        source_path: location.pathname,
                        destination_path: 'external',
                        is_dropdown: true,
                      });
                      // Keep legacy tracking for now
                      trackEvent({
                        event: 'help_onboarding_clicked',
                        platform: platform,
                      });
                      setDrawerOpen(false);
                    }}
                    sx={{
                      pl: 4,
                      fontWeight: 'normal',
                      color: 'textColors.lightHeader',
                      textDecoration: 'none',
                    }}
                  >
                    <ListItemText primary="Book Onboarding" />
                  </ListItem>
                  <ListItem
                    component="a"
                    href={`https://${brandLanguage.brandSite}/contact`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      // Track help menu navigation
                      navigationMetrics.clicked({
                        menu_item: 'help_contact',
                        source_path: location.pathname,
                        destination_path: 'external',
                        is_dropdown: true,
                      });
                      // Keep legacy tracking for now
                      trackEvent({
                        event: 'help_contactus_clicked',
                        platform: platform,
                      });
                      setDrawerOpen(false);
                    }}
                    sx={{
                      pl: 4,
                      fontWeight: 'normal',
                      color: 'textColors.lightHeader',
                      textDecoration: 'none',
                    }}
                  >
                    <ListItemText primary="Contact Support" />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => {
                        // Track help menu navigation
                        navigationMetrics.clicked({
                          menu_item: 'help_diagnostics',
                          source_path: location.pathname,
                          destination_path: '/diagnostics',
                          is_dropdown: true,
                        });
                        // Keep legacy tracking for now
                        trackEvent({
                          event: 'help_diagnostics_clicked',
                          platform: platform,
                        });
                        navigate('/diagnostics');
                        setDrawerOpen(false);
                      }}
                      sx={{ pl: 4, fontWeight: 'normal', color: 'textColors.lightHeader' }}
                    >
                      <ListItemText primary="Troubleshooting Tools" />
                    </ListItemButton>
                  </ListItem>
                </List>
              </Collapse>
            </List>
          </Box>

          {/* Drawer Footer with Logout Button */}
          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                localStorage.removeItem('accessToken');
                // Note: userInfo is no longer stored in localStorage - it's in RTK Query cache
                void logout({
                  logoutParams: {
                    returnTo: window.location.origin,
                  },
                });
                setDrawerOpen(false);
              }}
              sx={{
                fontWeight: 'normal',
                color: 'textColors.lightHeader',
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'primary.main',
                  color: 'primary.main',
                },
              }}
            >
              Logout
            </Button>
          </Box>
        </Drawer>
        {UIState.navbar.showCopyNotification && (
          <div
            className={`tooltip-animation absolute z-40 top-0 left-0 inline-block p-4 mt-16 w-full font-medium text-black duration-300 transition-opacity bg-green-400 shadow-sm tooltip`}
          >
            Copied
          </div>
        )}
        <AlertBanner />
      </AppBar>
      {/* Email Verification Prompt */}
      <Modal
        open={showEmailVerificationModal}
        onClose={() => {
          setShowEmailVerificationModal(false);
        }}
      >
        <Box>
          <EmailVerificationPrompt
            onClose={() => {
              setShowEmailVerificationModal(false);
            }}
          />
        </Box>
      </Modal>
    </>
  );
};
